import { useRef, useEffect, useState, useMemo } from 'react';
import type { NFTLayerAsset } from '../../types';
import { preloadLayerImages, getDecodedImageFromCache } from '../../hooks/useLayerImage';

interface CropCanvasProps {
  size: number;
  imageUrl?: string; // Legacy single image mode
  layerAssets?: NFTLayerAsset[]; // New layer-based mode
  offset: { x: number; y: number }; // Pan offset from dragging (both X and Y)
  scale: number;
  showCircleMask: boolean;
  onImageLoad?: (width: number, height: number) => void;
  fixedLayerIds?: Set<string>; // User-toggled fixed layer IDs (overrides auto-detection)
  stickerMode?: boolean; // When true, skip background/frame layers for transparent sticker export
}

// Single source of truth lives in utils/layerUtils (checks id AND type).
// Re-exported here so existing `import { isFillLayerByDefault } from './CropCanvas'`
// call sites keep working.
import { isFillLayerByDefault } from '../../utils/layerUtils';
export { isFillLayerByDefault };

export function CropCanvas({
  size,
  imageUrl,
  layerAssets = [],
  offset,
  scale,
  showCircleMask,
  onImageLoad,
  fixedLayerIds,
  stickerMode = false,
}: CropCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [layersLoaded, setLayersLoaded] = useState(false);

  // Use layer-based mode if layerAssets are provided and not empty
  const useLayerMode = layerAssets.length > 0;

  // Build render layers with fixed status
  // In sticker mode, filter out background/frame layers entirely
  const renderLayers = useMemo(() => {
    const layers = layerAssets.map((asset, index) => ({
      asset,
      order: index,
      isFixed: fixedLayerIds ? fixedLayerIds.has(asset.id) : isFillLayerByDefault(asset),
    }));

    // In sticker mode, skip background/frame layers
    if (stickerMode) {
      return layers.filter((layer) => !isFillLayerByDefault(layer.asset));
    }

    return layers;
  }, [layerAssets, fixedLayerIds, stickerMode]);

  // Preload layer images when layerAssets change
  useEffect(() => {
    if (useLayerMode && layerAssets.length > 0) {
      setLayersLoaded(false);
      preloadLayerImages(layerAssets).then(() => {
        setLayersLoaded(true);
      });
    } else {
      setLayersLoaded(true);
    }
  }, [layerAssets, useLayerMode]);

  // Load single image when URL changes (legacy mode)
  useEffect(() => {
    if (useLayerMode || !imageUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setImage(img);
      setImageLoaded(true);
      if (onImageLoad) {
        onImageLoad(img.width, img.height);
      }
    };

    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      setImageLoaded(false);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, onImageLoad, useLayerMode]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check if we have content to draw
    if (useLayerMode && !layersLoaded) return;
    if (!useLayerMode && (!imageLoaded || !image)) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw checkerboard pattern for transparency
    const squareSize = 20;
    for (let y = 0; y < canvas.height; y += squareSize) {
      for (let x = 0; x < canvas.width; x += squareSize) {
        const isLight = ((x + y) / squareSize) % 2 === 0;
        ctx.fillStyle = isLight ? '#2a2a3e' : '#1f1f2e';
        ctx.fillRect(x, y, squareSize, squareSize);
      }
    }

    if (useLayerMode) {
      // Layer-based rendering
      let primaryImageReported = false;

      for (const renderLayer of renderLayers) {
        const img = getDecodedImageFromCache(renderLayer.asset.uri);
        if (img) {
          ctx.save();

          // Enable antialiasing for smooth resizing
          ctx.imageSmoothingEnabled = true;

          if (renderLayer.isFixed) {
            // Fill layers: ALWAYS cover the entire canvas at 100%
            // They do NOT scale with user zoom - they always fill completely
            const imageAspect = img.width / img.height;
            const canvasAspect = canvas.width / canvas.height;

            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > canvasAspect) {
              // Image is wider - fit to height
              drawHeight = canvas.height;
              drawWidth = drawHeight * imageAspect;
            } else {
              // Image is taller or same - fit to width
              drawWidth = canvas.width;
              drawHeight = drawWidth / imageAspect;
            }

            // Always center (no pan offset for fill layers)
            const drawX = (canvas.width - drawWidth) / 2;
            const drawY = (canvas.height - drawHeight) / 2;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          } else {
            // Standard layers: transform with pan/zoom (user can drag to position)
            // Report the first standard layer's dimensions for bounds calculation
            if (!primaryImageReported && onImageLoad) {
              onImageLoad(img.width, img.height);
              primaryImageReported = true;
            }

            // Apply pan offset and scale
            const drawWidth = canvas.width * scale;
            const drawHeight = canvas.height * scale;

            // Center on canvas and apply drag offset (both X and Y)
            const drawX = (canvas.width - drawWidth) / 2 + offset.x;
            const drawY = (canvas.height - drawHeight) / 2 + offset.y;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }

          ctx.restore();
        }
      }
    } else {
      // Legacy single image rendering
      ctx.save();

      // Enable antialiasing for smooth resizing
      ctx.imageSmoothingEnabled = true;

      // Calculate draw dimensions maintaining aspect ratio
      const imageAspect = image!.width / image!.height;
      let drawWidth: number;
      let drawHeight: number;

      if (imageAspect > 1) {
        // Image is wider - fit to height first, then scale
        drawHeight = canvas.height * scale;
        drawWidth = drawHeight * imageAspect;
      } else {
        // Image is taller or square - fit to width first, then scale
        drawWidth = canvas.width * scale;
        drawHeight = drawWidth / imageAspect;
      }

      // Center on canvas and apply drag offset (both X and Y)
      const drawX = (canvas.width - drawWidth) / 2 + offset.x;
      const drawY = (canvas.height - drawHeight) / 2 + offset.y;

      ctx.drawImage(image!, drawX, drawY, drawWidth, drawHeight);

      ctx.restore();
    }

    // Draw circle mask overlay if enabled (for PFP preview)
    // This helps users visualize how their PFP will look in apps with circular avatars
    // The actual export remains square - this is just a visual preview
    if (showCircleMask) {
      ctx.save();

      // Draw semi-transparent dark overlay outside the circle
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(canvas.width, canvas.height) / 2;

      // Create a path that covers the entire canvas with a circular hole
      ctx.beginPath();
      // Outer rectangle (full canvas)
      ctx.rect(0, 0, canvas.width, canvas.height);
      // Inner circle (cut out) - drawn counter-clockwise to create a hole
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true);
      ctx.closePath();

      // Fill the area outside the circle with semi-transparent dark overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fill();

      // Draw a subtle circle border for clarity
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - 1, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.restore();
    }
  }, [image, imageLoaded, layersLoaded, useLayerMode, renderLayers, size, offset, scale, showCircleMask, onImageLoad, stickerMode]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="rounded-lg border border-surface-border shadow-lg"
      style={{ width: size, height: size }}
    />
  );
}
